import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  LinkOff as LinkOffIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { User } from '../types/user';

function isUserAlreadyOnProvider(user: User, providerId: number): boolean {
  const managed = user.managed_location_providers;
  if (!managed?.length) return false;
  return managed.some((p) => Number(p.id) === Number(providerId));
}

interface ProviderUser {
  id: number;
  email: string;
  login_username?: string | null;
  is_temporary_access?: boolean;
  access_expires_at?: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  isActive: boolean;
  roles: string[];
  createdAt: string;
}

interface ProviderUserManagerProps {
  providerId: number | null;
  providerName?: string;
  onUserAdded?: (user: ProviderUser) => void;
  onUserRemoved?: (userId: number) => void;
}

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: number;
  onUserCreated: (user: ProviderUser) => void;
}

interface AssociateUserDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: number;
  onUserAssociated: (user: ProviderUser) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  providerId,
  onUserCreated,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    sendAccessEmail: true,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: any) => apiService.createProviderUser(data),
    onSuccess: (response) => {
      onUserCreated(response.data.user ?? response.data);
      onClose();
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        sendAccessEmail: true,
        isActive: true,
      });
      setErrors({});
    },
    onError: (error: any) => {
      if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      }
    },
  });

  const generatePasswordMutation = useMutation({
    mutationFn: () => apiService.generateProviderUserPassword(),
    onSuccess: (response) => {
      setFormData(prev => ({ ...prev, password: response.data.password }));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email requis';
    if (!formData.firstName) newErrors.firstName = 'Prénom requis';
    if (!formData.lastName) newErrors.lastName = 'Nom requis';
    if (formData.password) {
      if (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/\d/.test(formData.password)) {
        newErrors.password = 'Minimum 8 caracteres, 1 majuscule et 1 chiffre';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    createMutation.mutate({
      ...formData,
      provider_id: providerId,
      send_access_email: formData.sendAccessEmail,
    });
  };

  const handleGeneratePassword = () => {
    generatePasswordMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ajouter un utilisateur</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Prénom"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            error={!!errors.firstName}
            helperText={errors.firstName}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Nom"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            error={!!errors.lastName}
            helperText={errors.lastName}
            margin="normal"
            required
          />
          
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              error={!!errors.password}
              helperText={errors.password || 'Optionnel: laisser vide pour forcer le choix via email'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={handleGeneratePassword}
              disabled={generatePasswordMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {generatePasswordMutation.isPending ? (
                <CircularProgress size={20} />
              ) : (
                'Générer'
              )}
            </Button>
          </Box>
          <TextField
            fullWidth
            label="Confirmer le mot de passe"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end">
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sendAccessEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, sendAccessEmail: e.target.checked }))}
              />
            }
            label="Envoyer un email d'acces au dashboard partenaire (choix du mot de passe)"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? <CircularProgress size={20} /> : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const AssociateUserDialog: React.FC<AssociateUserDialogProps> = ({
  open,
  onClose,
  providerId,
  onUserAssociated,
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sendAccessEmail, setSendAccessEmail] = useState(true);

  const { data: usersData = [], isFetching } = useQuery({
    queryKey: ['users', 'associate-candidates', providerId],
    queryFn: async (): Promise<User[]> => {
      const pageSize = 500;
      const acc: User[] = [];
      for (let page = 1; page <= 80; page += 1) {
        const response = await apiService.getUsers({ limit: pageSize, page });
        const batch: User[] = response.data?.users ?? [];
        acc.push(...batch);
        if (batch.length < pageSize) break;
      }
      return acc;
    },
    enabled: open,
    staleTime: 120_000,
  });

  const associateMutation = useMutation({
    mutationFn: (data: any) => apiService.associateProviderUser(data.userId, data.providerId, data.sendAccessEmail),
    onSuccess: (response) => {
      onUserAssociated(response.data.user);
      onClose();
      setSelectedUser(null);
      setSendAccessEmail(true);
      setErrors({});
    },
    onError: (error: any) => {
      if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUser) {
      setErrors({ user: 'Veuillez sélectionner un utilisateur' });
      return;
    }

    associateMutation.mutate({
      userId: selectedUser.id,
      providerId,
      sendAccessEmail,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Associer un utilisateur existant</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}
          
          <Autocomplete
            fullWidth
            loading={isFetching}
            options={usersData}
            getOptionDisabled={(option) => isUserAlreadyOnProvider(option, providerId)}
            getOptionLabel={(option) => {
              const label = (option.fullName || `${option.firstName ?? ''} ${option.lastName ?? ''}`).trim();
              return `${label || option.email} (${option.email})`;
            }}
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
              setErrors({});
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sélectionner un utilisateur"
                error={!!errors.user}
                helperText={
                  errors.user ||
                  'Tapez pour filtrer. Les comptes déjà rattachés à ce partenaire sont grisés. Pour un autre partenaire, l’ancienne association sera levée à l’association.'
                }
                margin="normal"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              const alreadyHere = isUserAlreadyOnProvider(option, providerId);
              return (
                <Box key={key} component="li" {...optionProps}>
                  <Box>
                    <Typography variant="body1" component="span">
                      {(option.fullName || `${option.firstName ?? ''} ${option.lastName ?? ''}`).trim() || option.email}
                      {alreadyHere ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (déjà sur ce partenaire)
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.email}
                    </Typography>
                    {option.managed_location_providers && option.managed_location_providers.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Partenaire(s) :{' '}
                        {option.managed_location_providers.map((p: { id: number; name: string }) => p.name).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={sendAccessEmail}
                onChange={(e) => setSendAccessEmail(e.target.checked)}
              />
            }
            label="Envoyer un email d'acces pour definir le mot de passe"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={associateMutation.isPending}
          >
            {associateMutation.isPending ? <CircularProgress size={20} /> : 'Associer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ProviderUserManager: React.FC<ProviderUserManagerProps> = ({
  providerId,
  providerName,
  onUserAdded,
  onUserRemoved,
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [associateDialogOpen, setAssociateDialogOpen] = useState(false);
  const [detachDialogOpen, setDetachDialogOpen] = useState(false);
  const [userToDetach, setUserToDetach] = useState<ProviderUser | null>(null);
  const [detachError, setDetachError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['provider-users', { providerId }],
    queryFn: () => apiService.getProviderUsers({ providerId }),
    enabled: !!providerId,
    select: (response) =>
      ((response.data.users || []) as ProviderUser[]).filter((u) => !u.is_temporary_access),
  });

  const detachMutation = useMutation({
    mutationFn: ({ userId, providerId }: { userId: number; providerId: number }) =>
      apiService.detachProviderUser(userId, providerId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      setDetachDialogOpen(false);
      setUserToDetach(null);
      setDetachError(null);
      if (onUserRemoved) {
        onUserRemoved(userId);
      }
    },
    onError: (error: any) => {
      const status = error.response?.status;
      const apiError = error.response?.data?.error;
      if (status === 404 && !apiError) {
        setDetachError(
          'Le détachement n’est pas encore disponible sur cette API. Déployez la dernière version du backend Symfony sur api26.sherlook.fr.'
        );
        return;
      }
      setDetachError(apiError || 'Impossible de détacher cet utilisateur.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: number; isActive: boolean }) => {
      return isActive 
        ? apiService.activateProviderUser(userId)
        : apiService.deactivateProviderUser(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
    },
  });

  const handleDetachUser = (user: ProviderUser) => {
    setDetachError(null);
    setUserToDetach(user);
    setDetachDialogOpen(true);
  };

  const confirmDetachUser = () => {
    if (!userToDetach || !providerId) return;
    detachMutation.mutate({ userId: userToDetach.id, providerId });
  };

  const permanentUserCount = usersData?.length ?? 0;
  const canDetachUsers = permanentUserCount > 1;

  const handleToggleActive = (userId: number, isActive: boolean) => {
    toggleActiveMutation.mutate({ userId, isActive: !isActive });
  };

  const handleUserCreated = (user: ProviderUser) => {
    queryClient.invalidateQueries({ queryKey: ['provider-users'] });
    if (onUserAdded) {
      onUserAdded(user);
    }
  };

  const handleUserAssociated = (user: ProviderUser) => {
    queryClient.invalidateQueries({ queryKey: ['provider-users'] });
    if (onUserAdded) {
      onUserAdded(user);
    }
  };

  if (!providerId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          Sélectionnez un partenaire pour gérer ses utilisateurs
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Utilisateurs du partenaire {providerName && `"${providerName}"`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setAssociateDialogOpen(true)}
          >
            Associer un utilisateur
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Créer un utilisateur
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {usersData?.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="Aucun utilisateur"
                secondary="Ajoutez des utilisateurs pour qu'ils puissent accéder au dashboard partenaire"
              />
            </ListItem>
          ) : (
            usersData?.map((user: ProviderUser) => (
              <ListItem key={user.id} divider>
                <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" component="span">
                        {user.fullName}
                      </Typography>
                      <Chip
                        label={user.isActive ? 'Actif' : 'Inactif'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <EmailIcon fontSize="small" />
                        <Typography variant="body2" color="text.secondary" component="span">
                          {user.email}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" component="span">
                        Créé le {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() => handleToggleActive(user.id, user.isActive)}
                    disabled={toggleActiveMutation.isPending}
                    color={user.isActive ? 'warning' : 'success'}
                    title={user.isActive ? 'Désactiver' : 'Activer'}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDetachUser(user)}
                    disabled={!canDetachUsers || detachMutation.isPending}
                    color="error"
                    title={
                      canDetachUsers
                        ? 'Détacher du partenaire'
                        : 'Impossible de détacher le seul utilisateur du partenaire'
                    }
                  >
                    <LinkOffIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>
      )}

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        providerId={providerId}
        onUserCreated={handleUserCreated}
      />

      <AssociateUserDialog
        open={associateDialogOpen}
        onClose={() => setAssociateDialogOpen(false)}
        providerId={providerId}
        onUserAssociated={handleUserAssociated}
      />

      <Dialog
        open={detachDialogOpen}
        onClose={() => {
          if (!detachMutation.isPending) {
            setDetachDialogOpen(false);
            setUserToDetach(null);
            setDetachError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Détacher du partenaire</DialogTitle>
        <DialogContent>
          {detachError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detachError}
            </Alert>
          )}
          <Typography>
            Détacher <strong>{userToDetach?.fullName}</strong> du partenaire {providerName && `"${providerName}"`} ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Le compte Sherlook sera conservé. L’utilisateur ne pourra plus accéder au dashboard de ce partenaire,
            mais pourra être réassocié plus tard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDetachDialogOpen(false);
              setUserToDetach(null);
              setDetachError(null);
            }}
            disabled={detachMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmDetachUser}
            color="error"
            variant="contained"
            disabled={detachMutation.isPending}
          >
            {detachMutation.isPending ? <CircularProgress size={20} /> : 'Détacher'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderUserManager;
